import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  RefreshCw, Plus, Loader2, CheckCircle2, Clock, AlertTriangle, XCircle,
  Circle, MonitorCheck, Wifi, Server, Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  operational: { label: 'Operational', color: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  degraded: { label: 'Degraded', color: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  down: { label: 'Down', color: 'bg-red-500', badge: 'bg-red-100 text-red-700', icon: XCircle },
  maintenance: { label: 'Maintenance', color: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700', icon: Clock },
};

export default function SystemStatus({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', platform_type_id: '', status: 'operational', notes: '' });
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterProp, setFilterProp] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [s, p, pl] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/system-status`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setData(s.data);
      setProperties(p.data || []);
      setPlatforms(pl.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/system-status`, form, { headers });
      toast.success('Status updated');
      setUpdateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};
  const filtered = filterProp && filterProp !== '__all__' ? (data?.statuses || []).filter(x => x.property_id === filterProp) : (data?.statuses || []);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="system-status">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>System Status Dashboard</h2>
          <p className="text-sm text-muted-foreground">Real-time operational status across all properties.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1 text-xs"><RefreshCw className="h-3 w-3" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => setUpdateOpen(true)} className="gap-1 text-xs" data-testid="update-status-btn"><Plus className="h-3 w-3" /> Update Status</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(STATUS_META).map(([key, meta]) => {
          const count = key === 'operational' ? s.operational || 0 : key === 'down' ? s.down || 0 : key === 'degraded' ? s.degraded || 0 : 0;
          return (
            <div key={key} className="p-3 rounded-lg border bg-card flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${meta.color}`} />
              <div>
                <div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{count}</div>
                <div className="text-[10px] text-muted-foreground">{meta.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Property filter */}
      <Select value={filterProp} onValueChange={setFilterProp}>
        <SelectTrigger className="w-64 h-8 text-sm"><SelectValue placeholder="All properties" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Properties</SelectItem>
          {properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Status list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No status records yet. Use "Update Status" to track system health.</p>
        ) : filtered.map((item, i) => {
          const meta = STATUS_META[item.status] || STATUS_META.operational;
          const Icon = meta.icon;
          return (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/20">
              <div className={`w-2.5 h-2.5 rounded-full ${meta.color} shrink-0`} />
              <Icon className={`h-4 w-4 shrink-0 ${meta.badge.split(' ')[1]}`} />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{item.platform_name}</span>
                <span className="text-xs text-muted-foreground ml-2">at {item.property_name}</span>
              </div>
              <Badge className={`text-[10px] ${meta.badge}`}>{meta.label}</Badge>
              {item.notes && <span className="text-xs text-muted-foreground max-w-[200px] truncate">{item.notes}</span>}
            </div>
          );
        })}
      </div>

      <Dialog open={updateOpen} onOpenChange={setUpdateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Update System Status</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Platform</label>
              <Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Status</label>
              <div className="flex gap-2 mt-1">
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <button key={key} onClick={() => setForm(p => ({ ...p, status: key }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${form.status === key ? meta.badge + ' border-current' : 'hover:bg-muted/60'}`}>
                    <div className={`w-2 h-2 rounded-full ${meta.color}`} /> {meta.label}
                  </button>
                ))}
              </div>
            </div>
            <div><label className="text-sm font-medium">Notes</label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Intermittent connectivity issues..." className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.property_id || !form.platform_type_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
